<template>
  <div class="space-y-6">
    <!-- Top Filter & Refresh Banner -->
    <div
      class="bg-white dark:bg-[#111C30] p-4 sm:p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4"
    >
      <div class="flex items-center space-x-3">
        <div
          class="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/20 shrink-0"
        >
          <BarChart3 class="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div>
          <h2 class="text-base sm:text-xl font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight">
            Class Fee Analytics & Defaulters Tracker
          </h2>
          <p class="text-xs sm:text-xs text-slate-500 dark:text-[#A6B4C8] font-medium">
            Compare expected fees vs actual collections by class and identify irregular paying students
          </p>
        </div>
      </div>

      <!-- Controls: Academic Session & Refresh -->
      <div class="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
        <div
          class="flex items-center space-x-2 bg-slate-50 dark:bg-[#172235] px-3 py-2 rounded-xl border border-slate-200 dark:border-[#263449] flex-1 sm:flex-initial"
        >
          <span class="text-xs font-semibold text-slate-500 dark:text-[#A6B4C8] shrink-0">Session:</span>
          <select
            v-model="activeSession"
            @change="fetchAnalytics"
            class="bg-transparent text-xs font-bold text-slate-800 dark:text-[#F1F5F9] outline-none cursor-pointer w-full sm:w-auto"
          >
            <option value="" class="dark:bg-[#172235]">All Sessions</option>
            <option v-for="s in availableSessions" :key="s" :value="s" class="dark:bg-[#172235]">
              {{ s }}
            </option>
          </select>
        </div>

        <button
          @click="fetchAnalytics"
          :disabled="isLoading"
          class="p-2 sm:p-2.5 rounded-xl bg-slate-100 dark:bg-[#18243A] hover:bg-slate-200 dark:hover:bg-[#172235] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] transition border border-slate-200 dark:border-[#263449] shrink-0 cursor-pointer"
          title="Refresh Analytics"
        >
          <RefreshCw :class="['w-4 h-4', isLoading ? 'animate-spin text-indigo-500' : '']" />
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading && !classesData.length"
      class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] p-12 text-center flex flex-col items-center justify-center min-h-[300px]"
    >
      <AppLoader
        variant="card"
        size="md"
        message="Loading Class Analytics..."
        subtext="Computing class expected vs collected amounts and analyzing student regularity"
        color="indigo"
      />
    </div>

    <template v-else>
      <!-- Summary KPI Metric Cards (6 Cards) -->
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-4">
        <!-- 1. Total Projected / Expected -->
        <div
          class="bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm relative overflow-hidden"
        >
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] sm:text-[11px] font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider truncate">
              Total Expected
            </span>
            <div class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
              <TrendingUp class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-sm sm:text-base lg:text-lg font-black text-slate-900 dark:text-[#F1F5F9] font-mono truncate">
            ₹{{ (summary.totalExpected || 0).toLocaleString("en-IN") }}
          </div>
          <span class="text-[9px] sm:text-[10px] text-slate-400 dark:text-[#7E8DA5] font-medium truncate block">
            Annual: ₹{{ (summary.yearlyExpected || summary.totalExpected || 0).toLocaleString("en-IN") }} • Mo. Avg: ₹{{ (summary.monthlyAverageExpected || Math.round((summary.totalMonthlyExpected || 0) / 12) || 0).toLocaleString("en-IN") }}
          </span>
        </div>

        <!-- 2. Total Collected ("Got This") -->
        <div
          class="bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm relative overflow-hidden"
        >
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] sm:text-[11px] font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider truncate">
              Got / Collected
            </span>
            <div class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
              <CheckCircle2 class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-sm sm:text-base lg:text-lg font-black text-emerald-600 dark:text-emerald-400 font-mono truncate">
            ₹{{ (summary.totalCollected || 0).toLocaleString("en-IN") }}
          </div>
          <span class="text-[9px] sm:text-[10px] text-emerald-600 dark:text-emerald-400 font-medium truncate block">
            Annual: ₹{{ (summary.yearlyCollected || summary.totalCollected || 0).toLocaleString("en-IN") }} • Mo. Avg: ₹{{ (summary.monthlyAverageCollected || Math.round((summary.totalMonthlyCollected || 0) / 12) || 0).toLocaleString("en-IN") }}
          </span>
        </div>

        <!-- 3. Overall Shortfall / Deficit -->
        <div
          class="bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm relative overflow-hidden"
        >
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] sm:text-[11px] font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider truncate">
              Total Deficit / Gap
            </span>
            <div class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-red-50 dark:bg-rose-950/40 text-red-600 dark:text-rose-400 flex items-center justify-center shrink-0">
              <AlertCircle class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-sm sm:text-base lg:text-lg font-black text-red-600 dark:text-rose-400 font-mono truncate">
            ₹{{ (summary.totalOutstanding || 0).toLocaleString("en-IN") }}
          </div>
          <span class="text-[9px] sm:text-[10px] text-red-500 dark:text-rose-400 font-medium truncate block">
            Uncollected balance
          </span>
        </div>

        <!-- 4. Realization Rate % -->
        <div
          class="bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm relative overflow-hidden"
        >
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] sm:text-[11px] font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider truncate">
              Collection Rate
            </span>
            <div class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400 flex items-center justify-center shrink-0">
              <PieChart class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-sm sm:text-base lg:text-lg font-black text-teal-600 dark:text-teal-400 font-mono truncate">
            {{ summary.overallCollectionRate }}%
          </div>
          <!-- Mini Progress Bar -->
          <div class="w-full bg-slate-100 dark:bg-[#18243A] h-1.5 rounded-full mt-1 overflow-hidden">
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="
                summary.overallCollectionRate >= 80
                  ? 'bg-emerald-500'
                  : summary.overallCollectionRate >= 50
                  ? 'bg-amber-500'
                  : 'bg-rose-500'
              "
              :style="{ width: `${Math.min(100, Math.max(0, summary.overallCollectionRate))}%` }"
            ></div>
          </div>
        </div>

        <!-- 5. Irregular / Defaulters Count -->
        <div
          class="bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm relative overflow-hidden"
        >
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] sm:text-[11px] font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider truncate">
              Irregular Students
            </span>
            <div class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
              <Users class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-sm sm:text-base lg:text-lg font-black text-amber-600 dark:text-amber-400 font-mono truncate">
            {{ summary.totalIrregularStudentsCount }}
          </div>
          <span class="text-[9px] sm:text-[10px] text-amber-600 dark:text-amber-400 font-medium truncate block">
            {{ summary.chronicDefaultersCount }} Chronic (3+ Mos)
          </span>
        </div>

        <!-- 6. Top Class / Highlights -->
        <div
          class="bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm relative overflow-hidden"
        >
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] sm:text-[11px] font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider truncate">
              Best Performing
            </span>
            <div class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
              <Award class="w-3.5 h-3.5" />
            </div>
          </div>
          <div class="text-xs sm:text-sm font-black text-slate-900 dark:text-[#F1F5F9] truncate">
            {{ summary.bestPerformingClass?.name || "N/A" }}
          </div>
          <span class="text-[9px] sm:text-[10px] text-purple-600 dark:text-purple-400 font-bold truncate block">
            {{ summary.bestMonth ? `Best Mo: ${summary.bestMonth.name} (${summary.bestMonth.rate}%)` : (summary.bestPerformingClass ? `${summary.bestPerformingClass.rate}% collected` : "No fees data") }}
          </span>
        </div>
      </div>

      <!-- SECTION 1: MODERN GRAPH OF CLASSES (EXPECTED VS GOT THIS) WITH MONTHLY & YEARLY INFO -->
      <div class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm overflow-hidden p-4 sm:p-6 space-y-4">
        <!-- View Mode Switcher Tabs (By Class | Monthly Trend | Yearly Overview) -->
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-[#1E293B]">
          <div class="inline-flex rounded-2xl bg-slate-100 dark:bg-[#18243A] p-1 border border-slate-200/80 dark:border-[#263449]">
            <button
              @click="chartMode = 'class'"
              :class="[
                'px-3 sm:px-4 py-1.5 text-xs font-black rounded-xl transition-all flex items-center gap-1.5 cursor-pointer',
                chartMode === 'class'
                  ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'
              ]"
            >
              <Building2 class="w-3.5 h-3.5" />
              <span>By Class</span>
            </button>
            <button
              @click="chartMode = 'monthly'"
              :class="[
                'px-3 sm:px-4 py-1.5 text-xs font-black rounded-xl transition-all flex items-center gap-1.5 cursor-pointer',
                chartMode === 'monthly'
                  ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'
              ]"
            >
              <CalendarDays class="w-3.5 h-3.5" />
              <span>Monthly Trend</span>
            </button>
            <button
              @click="chartMode = 'yearly'"
              :class="[
                'px-3 sm:px-4 py-1.5 text-xs font-black rounded-xl transition-all flex items-center gap-1.5 cursor-pointer',
                chartMode === 'yearly'
                  ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'
              ]"
            >
              <CalendarRange class="w-3.5 h-3.5" />
              <span>Yearly Overview</span>
            </button>
          </div>

          <div class="text-[11px] text-slate-500 dark:text-[#A6B4C8] font-medium flex items-center gap-2">
            <span class="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span v-if="chartMode === 'class'">Class comparison with monthly & yearly realization</span>
            <span v-else-if="chartMode === 'monthly'">12-month expected vs received realization schedule</span>
            <span v-else>Multi-year historical & session realization overview</span>
          </div>
        </div>

        <!-- Dynamic Chart Title & Controls Bar -->
        <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-2 border-b border-slate-100 dark:border-[#1E293B]">
          <div>
            <div class="flex items-center gap-2">
              <h3 class="text-base sm:text-lg font-black text-slate-900 dark:text-[#F1F5F9]">
                {{
                  chartMode === 'class'
                    ? 'Class Revenue Realization: Expected vs Received'
                    : chartMode === 'monthly'
                    ? 'Monthly Revenue Realization: Expected vs Received Trend'
                    : 'Yearly / Multi-Session Fee Comparison'
                }}
              </h3>
              <span class="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/60">
                Live Chart
              </span>
            </div>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] mt-0.5 font-medium">
              {{
                chartMode === 'class'
                  ? 'Comparing what was expected from each class (e.g. Class 1) against actual payments collected ("got this")'
                  : chartMode === 'monthly'
                  ? '12-month fee realization schedule showing monthly expected dues vs actual payments received'
                  : 'Annual revenue realization and recovery totals compared across academic sessions'
              }}
            </p>
          </div>

          <!-- Dynamic Controls Bar -->
          <div class="flex flex-wrap items-center gap-2 sm:gap-2.5">
            <!-- Fee Scope (Only in Class or Yearly modes) -->
            <div
              v-if="chartMode !== 'monthly'"
              class="inline-flex rounded-xl bg-slate-100 dark:bg-[#18243A] p-0.5 border border-slate-200/80 dark:border-[#263449]"
            >
              <button
                @click="feeScope = 'total'"
                :class="[
                  'px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer',
                  feeScope === 'total'
                    ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs'
                    : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]',
                ]"
              >
                Total
              </button>
              <button
                @click="feeScope = 'monthly'"
                :class="[
                  'px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer',
                  feeScope === 'monthly'
                    ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs'
                    : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]',
                ]"
              >
                Monthly
              </button>
              <button
                @click="feeScope = 'admission'"
                :class="[
                  'px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer',
                  feeScope === 'admission'
                    ? 'bg-white dark:bg-[#111C30] text-indigo-600 dark:text-indigo-400 shadow-xs'
                    : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]',
                ]"
              >
                Admission
              </button>
            </div>

            <!-- Class Mode: Period Filter (Annual vs Month) -->
            <select
              v-if="chartMode === 'class'"
              v-model="selectedClassMonth"
              class="px-2.5 py-1.5 text-[11px] font-bold rounded-xl bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] border border-slate-200/80 dark:border-[#263449] outline-none cursor-pointer"
            >
              <option :value="0">Period: All Year (Annual)</option>
              <option v-for="m in monthlyData" :key="m.month" :value="m.month">
                Period: {{ m.monthName }}
              </option>
            </select>

            <!-- Monthly Mode: Filter by Class -->
            <select
              v-if="chartMode === 'monthly'"
              v-model="selectedMonthlyClass"
              class="px-2.5 py-1.5 text-[11px] font-bold rounded-xl bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] border border-slate-200/80 dark:border-[#263449] outline-none cursor-pointer"
            >
              <option value="">Filter: All Classes</option>
              <option v-for="c in classesData" :key="c.classId" :value="c.classId">
                {{ c.displayName }}
              </option>
            </select>

            <!-- Chart Orientation (Columns vs Horizontal Bars) -->
            <button
              @click="toggleOrientation"
              class="px-2.5 py-1.5 text-xs font-semibold rounded-xl bg-slate-100 dark:bg-[#18243A] hover:bg-slate-200 dark:hover:bg-[#1E293B] text-slate-700 dark:text-[#A6B4C8] border border-slate-200/80 dark:border-[#263449] transition cursor-pointer flex items-center gap-1.5"
              :title="chartOrientation === 'vertical' ? 'Switch to Horizontal Bars' : 'Switch to Vertical Columns'"
            >
              <ArrowUpDown class="w-3.5 h-3.5" />
              <span class="text-[11px] font-bold hidden sm:inline">
                {{ chartOrientation === 'vertical' ? 'Horizontal' : 'Vertical' }}
              </span>
            </button>

            <!-- Sort By Selector (Only in Class Mode) -->
            <select
              v-if="chartMode === 'class'"
              v-model="chartSort"
              class="px-2.5 py-1.5 text-[11px] font-bold rounded-xl bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] border border-slate-200/80 dark:border-[#263449] outline-none cursor-pointer"
            >
              <option value="order">Sort: Class Order</option>
              <option value="expected_desc">Sort: Highest Expected</option>
              <option value="collected_desc">Sort: Highest Received</option>
              <option value="deficit_desc">Sort: Highest Deficit</option>
              <option value="rate_desc">Sort: Best Collection %</option>
              <option value="rate_asc">Sort: Lowest Collection %</option>
            </select>
          </div>
        </div>

        <!-- Custom Interactive Legend -->
        <div class="flex flex-wrap items-center justify-between gap-3 pt-1">
          <div class="flex items-center gap-4 text-xs font-bold">
            <div class="flex items-center gap-1.5">
              <span class="w-3.5 h-3.5 rounded-md bg-indigo-500 shadow-xs inline-block"></span>
              <span class="text-slate-700 dark:text-slate-300">Expected Fee</span>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="w-3.5 h-3.5 rounded-md bg-emerald-500 shadow-xs inline-block"></span>
              <span class="text-slate-700 dark:text-slate-300">Got / Received Fee</span>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="w-3.5 h-3.5 rounded-md bg-rose-400/60 border border-rose-500 inline-block"></span>
              <span class="text-slate-500 dark:text-[#7E8DA5]">Shortfall / Uncollected</span>
            </div>
          </div>

          <span class="text-[11px] text-slate-400 dark:text-[#7E8DA5] font-medium hidden sm:inline">
            Hover over bars for exact amounts and collection percentages
          </span>
        </div>

        <!-- Canvas Container -->
        <div
          class="relative w-full rounded-xl bg-slate-50/50 dark:bg-[#0B1120]/40 p-2 sm:p-4 border border-slate-100 dark:border-[#1E293B]"
          :style="{ minHeight: chartContainerHeight }"
        >
          <div
            v-if="(chartMode === 'class' && !sortedClasses.length) || (chartMode === 'monthly' && !monthlyData.length) || (chartMode === 'yearly' && !yearlyData.length)"
            class="h-64 flex items-center justify-center text-slate-400 text-xs font-medium"
          >
            No fee realization data found for this selection.
          </div>
          <canvas ref="chartCanvas" class="w-full h-full"></canvas>
        </div>

        <!-- VIEW 1: Class Breakdown Responsive Cards Grid -->
        <div v-if="chartMode === 'class'" class="pt-4 border-t border-slate-100 dark:border-[#1E293B]">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h4 class="text-xs sm:text-sm font-bold text-slate-800 dark:text-[#F1F5F9] uppercase tracking-wider">
                Class-by-Class Financial Realization
              </h4>
              <p class="text-[11px] text-slate-400 dark:text-[#7E8DA5]">
                Annual obligations and monthly rate realization per class
              </p>
            </div>
            <span class="text-[11px] text-slate-400 dark:text-[#7E8DA5] font-medium">
              {{ sortedClasses.length }} Classes
            </span>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            <div
              v-for="cls in sortedClasses"
              :key="cls.classId || cls.displayName"
              class="bg-white dark:bg-[#18243A] p-3.5 sm:p-4 rounded-xl border border-slate-200/80 dark:border-[#263449] hover:border-indigo-400 dark:hover:border-indigo-500/50 transition-all shadow-xs flex flex-col justify-between"
            >
              <div>
                <!-- Class Header & Students Count -->
                <div class="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <h5 class="text-sm font-black text-slate-900 dark:text-[#F1F5F9] truncate">
                      {{ cls.displayName }}
                    </h5>
                    <p class="text-[11px] text-slate-500 dark:text-[#A6B4C8] font-medium">
                      {{ cls.totalStudents }} students • {{ cls.configuredStudents }} configured
                    </p>
                  </div>
                  <span
                    class="px-2 py-0.5 rounded-full text-[10px] font-extrabold font-mono"
                    :class="
                      cls.collectionRate >= 80
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                        : cls.collectionRate >= 50
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                        : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                    "
                  >
                    {{ cls.collectionRate }}%
                  </span>
                </div>

                <!-- Progress Bar -->
                <div class="w-full bg-slate-100 dark:bg-[#111C30] h-2 rounded-full overflow-hidden mb-2.5">
                  <div
                    class="h-full rounded-full transition-all duration-300"
                    :class="
                      cls.collectionRate >= 80
                        ? 'bg-emerald-500'
                        : cls.collectionRate >= 50
                        ? 'bg-amber-500'
                        : 'bg-rose-500'
                    "
                    :style="{ width: `${Math.min(100, Math.max(0, cls.collectionRate))}%` }"
                  ></div>
                </div>

                <!-- Monthly Rate & Annual Total Sub-Badges -->
                <div class="flex items-center justify-between text-[10px] text-slate-500 dark:text-[#A6B4C8] mb-2 px-1 font-medium bg-slate-50 dark:bg-[#111C30]/40 py-1 rounded-md">
                  <span>Rate: <strong class="font-mono text-slate-700 dark:text-slate-300">₹{{ (cls.monthlyAverageExpected || Math.round((cls.monthlyExpected || 0) / 12) || 0).toLocaleString("en-IN") }}/mo</strong></span>
                  <span>Annual: <strong class="font-mono text-slate-700 dark:text-slate-300">₹{{ (cls.yearlyExpected || cls.totalExpected || 0).toLocaleString("en-IN") }}/yr</strong></span>
                </div>

                <!-- Expected vs Got breakdown -->
                <div class="grid grid-cols-2 gap-2 text-xs mb-3 bg-slate-50 dark:bg-[#111C30]/60 p-2 rounded-lg border border-slate-100 dark:border-[#263449]">
                  <div>
                    <span class="text-[10px] font-bold text-slate-400 dark:text-[#7E8DA5] uppercase block">Expected</span>
                    <span class="font-mono font-bold text-slate-800 dark:text-[#F1F5F9] text-xs">
                      ₹{{ (getScopeExpected(cls) || 0).toLocaleString("en-IN") }}
                    </span>
                  </div>
                  <div>
                    <span class="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase block">Got This</span>
                    <span class="font-mono font-black text-emerald-600 dark:text-emerald-400 text-xs">
                      ₹{{ (getScopeCollected(cls) || 0).toLocaleString("en-IN") }}
                    </span>
                  </div>
                  <div class="col-span-2 pt-1 border-t border-slate-200/60 dark:border-[#263449] flex items-center justify-between">
                    <span class="text-[10px] font-semibold text-rose-600 dark:text-rose-400">Shortfall / Due:</span>
                    <span class="font-mono font-bold text-rose-600 dark:text-rose-400 text-xs">
                      ₹{{ (getScopeOutstanding(cls) || 0).toLocaleString("en-IN") }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Action: View Defaulters for this Class -->
              <div class="flex items-center justify-between pt-1">
                <span
                  class="text-[10px] font-bold px-2 py-0.5 rounded-md"
                  :class="
                    cls.irregularStudentsCount > 0
                      ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200/60 dark:border-amber-800/40'
                      : 'bg-slate-100 dark:bg-[#111C30] text-slate-400 dark:text-[#7E8DA5]'
                  "
                >
                  {{ cls.irregularStudentsCount }} irregular
                </span>

                <button
                  @click="filterDefaultersByClass(cls.classId)"
                  class="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center gap-1 cursor-pointer transition"
                >
                  <span>Filter Students</span>
                  <ChevronRight class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- VIEW 2: Monthly Breakdown (Schedule Table & Mobile Cards) -->
        <div v-else-if="chartMode === 'monthly'" class="pt-4 border-t border-slate-100 dark:border-[#1E293B] space-y-3">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h4 class="text-xs sm:text-sm font-bold text-slate-800 dark:text-[#F1F5F9] uppercase tracking-wider flex items-center gap-1.5">
                <CalendarDays class="w-4 h-4 text-indigo-500" />
                12-Month Billing Realization Schedule
              </h4>
              <p class="text-[11px] text-slate-400 dark:text-[#7E8DA5]">
                Monthly expected dues vs collected fees across the academic cycle
              </p>
            </div>
            <span class="text-xs font-bold text-indigo-600 dark:text-indigo-400">
              Avg Monthly Expected: ₹{{ (summary.monthlyAverageExpected || 0).toLocaleString("en-IN") }}
            </span>
          </div>

          <!-- Mobile Cards View for Monthly -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 md:hidden">
            <div
              v-for="m in monthlyData"
              :key="m.month"
              class="bg-white dark:bg-[#18243A] p-3 rounded-xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-2"
            >
              <div class="flex items-center justify-between">
                <span class="font-bold text-sm text-slate-900 dark:text-[#F1F5F9]">{{ m.monthName }}</span>
                <span
                  class="px-2 py-0.5 rounded-full text-[10px] font-bold font-mono"
                  :class="
                    m.collectionRate >= 80
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                      : m.collectionRate >= 50
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                      : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                  "
                >
                  {{ m.collectionRate }}%
                </span>
              </div>
              <div class="grid grid-cols-2 gap-2 text-xs bg-slate-50 dark:bg-[#111C30]/50 p-2 rounded-lg">
                <div>
                  <span class="text-[10px] text-slate-400 block font-bold">Expected</span>
                  <span class="font-bold text-slate-800 dark:text-[#F1F5F9] font-mono">₹{{ (m.expected || 0).toLocaleString("en-IN") }}</span>
                </div>
                <div>
                  <span class="text-[10px] text-emerald-600 block font-bold">Got This</span>
                  <span class="font-bold text-emerald-600 dark:text-emerald-400 font-mono">₹{{ (m.collected || 0).toLocaleString("en-IN") }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Desktop Table for Monthly -->
          <div class="hidden md:block overflow-x-auto border border-slate-200/80 dark:border-[#263449] rounded-xl bg-white dark:bg-[#18243A]">
            <table class="w-full text-left text-xs min-w-[700px]">
              <thead class="bg-slate-50 dark:bg-[#111C30] text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[11px] border-b border-slate-200/80 dark:border-[#263449]">
                <tr>
                  <th class="py-3 px-4">#</th>
                  <th class="py-3 px-4">Billing Month</th>
                  <th class="py-3 px-4 text-right">Expected Revenue</th>
                  <th class="py-3 px-4 text-right">Got / Received</th>
                  <th class="py-3 px-4 text-right">Shortfall / Balance</th>
                  <th class="py-3 px-4 text-center">Collection Rate</th>
                  <th class="py-3 px-4 text-center">Students Due</th>
                  <th class="py-3 px-4 text-center">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 dark:divide-[#263449] font-medium">
                <tr
                  v-for="(m, idx) in monthlyData"
                  :key="m.month"
                  class="hover:bg-slate-50/70 dark:hover:bg-[#1A2840]/60 transition"
                >
                  <td class="py-3 px-4 font-mono text-slate-400">{{ String(idx + 1).padStart(2, '0') }}</td>
                  <td class="py-3 px-4 font-bold text-slate-900 dark:text-[#F1F5F9]">{{ m.monthName }}</td>
                  <td class="py-3 px-4 text-right font-mono font-bold text-slate-800 dark:text-[#F1F5F9]">₹{{ (m.expected || 0).toLocaleString("en-IN") }}</td>
                  <td class="py-3 px-4 text-right font-mono font-black text-emerald-600 dark:text-emerald-400">₹{{ (m.collected || 0).toLocaleString("en-IN") }}</td>
                  <td class="py-3 px-4 text-right font-mono font-bold" :class="m.balance > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'">₹{{ (m.balance || 0).toLocaleString("en-IN") }}</td>
                  <td class="py-3 px-4 text-center">
                    <div class="flex items-center justify-center gap-2">
                      <div class="w-16 bg-slate-100 dark:bg-[#111C30] h-2 rounded-full overflow-hidden">
                        <div
                          class="h-full rounded-full"
                          :class="m.collectionRate >= 80 ? 'bg-emerald-500' : m.collectionRate >= 50 ? 'bg-amber-500' : 'bg-rose-500'"
                          :style="{ width: `${Math.min(100, m.collectionRate)}%` }"
                        ></div>
                      </div>
                      <span class="font-mono font-bold text-xs">{{ m.collectionRate }}%</span>
                    </div>
                  </td>
                  <td class="py-3 px-4 text-center text-slate-600 dark:text-[#A6B4C8] font-bold">{{ m.studentCount }}</td>
                  <td class="py-3 px-4 text-center">
                    <span
                      class="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase inline-block"
                      :class="
                        m.collectionRate >= 85
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                          : m.collectionRate >= 50
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                          : m.expected === 0
                          ? 'bg-slate-100 text-slate-500 dark:bg-[#111C30] dark:text-[#7E8DA5]'
                          : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                      "
                    >
                      {{ m.expected === 0 ? 'No Dues' : m.collectionRate >= 85 ? 'Realized' : m.collectionRate >= 50 ? 'In Progress' : 'Lagging' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- VIEW 3: Yearly / Multi-Session Breakdown Grid -->
        <div v-else-if="chartMode === 'yearly'" class="pt-4 border-t border-slate-100 dark:border-[#1E293B] space-y-3">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-xs sm:text-sm font-bold text-slate-800 dark:text-[#F1F5F9] uppercase tracking-wider flex items-center gap-1.5">
                <CalendarRange class="w-4 h-4 text-indigo-500" />
                Multi-Year / Academic Session Realization
              </h4>
              <p class="text-[11px] text-slate-400 dark:text-[#7E8DA5]">
                Annual fee obligations and realization totals compared across academic sessions
              </p>
            </div>
            <span class="text-xs font-bold text-indigo-600 dark:text-indigo-400">
              Total Recorded Years: {{ yearlyData.length }}
            </span>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            <div
              v-for="y in yearlyData"
              :key="y.session"
              class="bg-white dark:bg-[#18243A] p-4 rounded-xl border transition-all shadow-xs flex flex-col justify-between"
              :class="
                y.isCurrent
                  ? 'border-indigo-500 ring-2 ring-indigo-500/10'
                  : 'border-slate-200/80 dark:border-[#263449]'
              "
            >
              <div>
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-1.5">
                    <span class="font-black text-sm text-slate-900 dark:text-[#F1F5F9]">{{ y.session }}</span>
                    <span
                      v-if="y.isCurrent"
                      class="px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300"
                    >
                      Active Session
                    </span>
                  </div>
                  <span
                    class="px-2 py-0.5 rounded-full text-[10px] font-extrabold font-mono"
                    :class="
                      y.collectionRate >= 80
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                        : y.collectionRate >= 50
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                        : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                    "
                  >
                    {{ y.collectionRate }}%
                  </span>
                </div>

                <!-- Progress Bar -->
                <div class="w-full bg-slate-100 dark:bg-[#111C30] h-2 rounded-full overflow-hidden mb-3">
                  <div
                    class="h-full rounded-full transition-all duration-300"
                    :class="y.collectionRate >= 80 ? 'bg-emerald-500' : y.collectionRate >= 50 ? 'bg-amber-500' : 'bg-rose-500'"
                    :style="{ width: `${Math.min(100, y.collectionRate)}%` }"
                  ></div>
                </div>

                <!-- Metric Grid -->
                <div class="grid grid-cols-2 gap-2 text-xs bg-slate-50 dark:bg-[#111C30]/60 p-2.5 rounded-lg border border-slate-100 dark:border-[#263449] mb-2">
                  <div>
                    <span class="text-[10px] font-bold text-slate-400 uppercase block">Annual Expected</span>
                    <span class="font-mono font-bold text-slate-800 dark:text-[#F1F5F9] text-xs">₹{{ (y.totalExpected || 0).toLocaleString("en-IN") }}</span>
                  </div>
                  <div>
                    <span class="text-[10px] font-bold text-emerald-600 uppercase block">Annual Received</span>
                    <span class="font-mono font-black text-emerald-600 dark:text-emerald-400 text-xs">₹{{ (y.totalCollected || 0).toLocaleString("en-IN") }}</span>
                  </div>
                  <div class="col-span-2 pt-1 border-t border-slate-200/60 dark:border-[#263449] flex items-center justify-between text-[11px]">
                    <span class="text-rose-600 font-semibold">Shortfall / Outstanding:</span>
                    <span class="font-mono font-bold text-rose-600">₹{{ (y.totalOutstanding || 0).toLocaleString("en-IN") }}</span>
                  </div>
                </div>

                <!-- Admission vs Monthly breakdown -->
                <div class="text-[10px] text-slate-500 dark:text-[#A6B4C8] space-y-0.5 px-1">
                  <div class="flex justify-between">
                    <span>Admission Collected:</span>
                    <strong class="font-mono">₹{{ (y.admissionCollected || 0).toLocaleString("en-IN") }}</strong>
                  </div>
                  <div class="flex justify-between">
                    <span>Monthly Collected:</span>
                    <strong class="font-mono">₹{{ (y.monthlyCollected || 0).toLocaleString("en-IN") }}</strong>
                  </div>
                </div>
              </div>

              <div class="pt-3 border-t border-slate-100 dark:border-[#263449] mt-2 flex items-center justify-between text-[11px] text-slate-400">
                <span>{{ y.studentsCount }} students</span>
                <span>{{ y.transactionCount }} transactions</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION 2: PAYMENT CONSISTENCY & DEFAULTERS TRACKER (STUDENTS NOT REGULARLY PAYING) -->
      <div
        ref="defaultersSectionRef"
        class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm overflow-hidden p-4 sm:p-6 space-y-4"
      >
        <!-- Defaulters Tracker Header -->
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-[#1E293B]">
          <div class="flex items-center space-x-3">
            <div
              class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 border border-amber-200/60 dark:border-amber-800/40"
            >
              <AlertTriangle class="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h3 class="text-base sm:text-lg font-black text-slate-900 dark:text-[#F1F5F9]">
                  Irregular & Inconsistent Paying Students
                </h3>
                <span
                  class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-rose-950/60 dark:text-rose-300 font-mono"
                >
                  {{ filteredIrregularStudents.length }}
                </span>
              </div>
              <p class="text-xs text-slate-500 dark:text-[#A6B4C8] font-medium">
                Students with chronic defaults (3+ months), skipped payments, and erratic monthly payment habits
              </p>
            </div>
          </div>

          <!-- Quick Risk Filter Pills -->
          <div class="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap scrollbar-none pb-0.5">
            <button
              @click="defaulterRiskFilter = 'all'"
              :class="[
                'px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer border',
                defaulterRiskFilter === 'all'
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 border-transparent shadow-xs'
                  : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] border-slate-200/80 dark:border-[#263449]',
              ]"
            >
              All Irregular ({{ irregularStudents.length }})
            </button>
            <button
              @click="defaulterRiskFilter = 'high'"
              :class="[
                'px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer border',
                defaulterRiskFilter === 'high'
                  ? 'bg-red-600 text-white border-red-600 shadow-xs'
                  : 'bg-red-50 dark:bg-rose-950/30 text-red-700 dark:text-rose-400 border-red-200/80 dark:border-rose-900/40',
              ]"
            >
              Chronic Defaulters (3+ Mos)
            </button>
            <button
              @click="defaulterRiskFilter = 'medium'"
              :class="[
                'px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer border',
                defaulterRiskFilter === 'medium'
                  ? 'bg-amber-600 text-white border-amber-600 shadow-xs'
                  : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200/80 dark:border-amber-900/40',
              ]"
            >
              Irregular (2 Mos)
            </button>
            <button
              @click="defaulterRiskFilter = 'low'"
              :class="[
                'px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer border',
                defaulterRiskFilter === 'low'
                  ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                  : 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-200/80 dark:border-blue-900/40',
              ]"
            >
              Delayed (1 Mo)
            </button>
          </div>
        </div>

        <!-- Filter & Search Controls Bar -->
        <div class="flex flex-col sm:flex-row items-center gap-3">
          <!-- Class Filter -->
          <div class="w-full sm:w-52">
            <label class="text-[10px] font-bold text-slate-500 dark:text-[#A6B4C8] uppercase tracking-wide block mb-1">
              Class Filter
            </label>
            <select
              v-model="defaulterClassFilter"
              class="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200/90 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] outline-none"
            >
              <option value="">All Classes</option>
              <option v-for="c in classesList" :key="c._id" :value="c._id">
                {{ c.name }}{{ c.section ? ` - ${c.section}` : "" }}
              </option>
            </select>
          </div>

          <!-- Student Search -->
          <div class="w-full sm:flex-1">
            <label class="text-[10px] font-bold text-slate-500 dark:text-[#A6B4C8] uppercase tracking-wide block mb-1">
              Search Student
            </label>
            <div class="relative">
              <Search class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                v-model="defaulterSearchQuery"
                type="text"
                placeholder="Search by student name, roll no, phone..."
                class="w-full pl-8 pr-3 py-2 text-xs font-semibold rounded-xl border border-slate-200/90 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] placeholder-slate-400 dark:placeholder-[#7E8DA5] outline-none"
              />
            </div>
          </div>

          <!-- Sorting -->
          <div class="w-full sm:w-48">
            <label class="text-[10px] font-bold text-slate-500 dark:text-[#A6B4C8] uppercase tracking-wide block mb-1">
              Sort Students
            </label>
            <select
              v-model="defaulterSort"
              class="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200/90 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] outline-none"
            >
              <option value="balance_desc">Highest Due Amount</option>
              <option value="overdue_desc">Most Overdue Months</option>
              <option value="score_asc">Lowest Regularity Score</option>
              <option value="name_asc">Student Name (A-Z)</option>
              <option value="roll_asc">Roll Number</option>
            </select>
          </div>

          <!-- Reset Button -->
          <div v-if="defaulterClassFilter || defaulterSearchQuery || defaulterRiskFilter !== 'all'" class="sm:self-end">
            <button
              @click="resetDefaulterFilters"
              class="px-3 py-2 text-xs font-bold text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] bg-slate-100 dark:bg-[#18243A] rounded-xl transition cursor-pointer border border-slate-200/80 dark:border-[#263449]"
            >
              Reset
            </button>
          </div>
        </div>

        <!-- Student Cards for Mobile (block md:hidden) -->
        <div class="block md:hidden space-y-3">
          <div
            v-for="s in filteredIrregularStudents"
            :key="'m-irr-' + s._id"
            class="bg-white dark:bg-[#18243A] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-3.5 shadow-xs relative overflow-hidden"
          >
            <!-- Left accent stripe -->
            <div
              class="absolute left-0 top-0 bottom-0 w-1.5"
              :class="
                s.riskLevel === 'high'
                  ? 'bg-red-500'
                  : s.riskLevel === 'medium'
                  ? 'bg-amber-500'
                  : 'bg-blue-500'
              "
            ></div>

            <div class="pl-2 space-y-2.5">
              <!-- Top Row: Student Avatar, Name, Class & Badge -->
              <div class="flex items-start justify-between gap-2">
                <div class="flex items-center space-x-2.5 min-w-0">
                  <div
                    class="w-10 h-10 rounded-xl bg-slate-100 dark:bg-[#111C30] flex items-center justify-center text-slate-500 dark:text-[#7E8DA5] overflow-hidden shrink-0 border border-slate-200/80 dark:border-[#263449]"
                  >
                    <img v-if="s.photo" :src="s.photo" alt="Photo" class="w-full h-full object-cover" />
                    <User v-else class="w-5 h-5 text-slate-400" />
                  </div>
                  <div class="min-w-0">
                    <h5 class="text-sm font-black text-slate-900 dark:text-[#F1F5F9] truncate">
                      {{ s.name }}
                    </h5>
                    <p class="text-[11px] text-slate-500 dark:text-[#A6B4C8] font-medium flex items-center gap-1">
                      <span class="font-bold text-slate-700 dark:text-slate-300">
                        {{ s.class?.name || "N/A" }}{{ s.class?.section ? ` (${s.class.section})` : "" }}
                      </span>
                      <span>•</span>
                      <span>Roll: <strong class="font-mono text-slate-700 dark:text-slate-300">{{ s.rollNumber || "N/A" }}</strong></span>
                    </p>
                  </div>
                </div>

                <!-- Consistency Status Pill -->
                <span
                  class="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase shrink-0"
                  :class="
                    s.riskLevel === 'high'
                      ? 'bg-red-100 text-red-800 dark:bg-rose-950/60 dark:text-rose-300 border border-red-200 dark:border-rose-900/50'
                      : s.riskLevel === 'medium'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-900/50'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-900/50'
                  "
                >
                  {{ s.consistencyLabel }}
                </span>
              </div>

              <!-- Financial Realization Row: Expected vs Got vs Outstanding -->
              <div class="grid grid-cols-3 gap-2 bg-slate-50 dark:bg-[#111C30]/70 p-2.5 rounded-xl border border-slate-100 dark:border-[#263449] text-center">
                <div>
                  <span class="text-[9px] uppercase font-bold text-slate-400 dark:text-[#7E8DA5] block">Expected</span>
                  <span class="font-mono font-bold text-xs text-slate-800 dark:text-[#F1F5F9]">
                    ₹{{ (s.totalExpected || 0).toLocaleString("en-IN") }}
                  </span>
                </div>
                <div>
                  <span class="text-[9px] uppercase font-bold text-emerald-600 dark:text-emerald-400 block">Got This</span>
                  <span class="font-mono font-black text-xs text-emerald-600 dark:text-emerald-400">
                    ₹{{ (s.totalPaid || 0).toLocaleString("en-IN") }}
                  </span>
                </div>
                <div>
                  <span class="text-[9px] uppercase font-bold text-rose-600 dark:text-rose-400 block">Balance Due</span>
                  <span class="font-mono font-black text-xs text-rose-600 dark:text-rose-400">
                    ₹{{ (s.totalOutstanding || 0).toLocaleString("en-IN") }}
                  </span>
                </div>
              </div>

              <!-- Unpaid Months Pill Tags -->
              <div v-if="s.unpaidMonths && s.unpaidMonths.length" class="space-y-1">
                <span class="text-[10px] font-bold text-slate-400 dark:text-[#7E8DA5] uppercase">
                  Pending / Overdue Months ({{ s.unpaidMonths.length }}):
                </span>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="m in s.unpaidMonths.slice(0, 4)"
                    :key="`${m.month}-${m.year}`"
                    class="text-[10px] font-bold font-mono px-2 py-0.5 rounded-md"
                    :class="
                      m.isOverdue
                        ? 'bg-red-50 text-red-700 dark:bg-rose-950/40 dark:text-rose-300 border border-red-200/60 dark:border-rose-900/40'
                        : 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200/60 dark:border-amber-900/40'
                    "
                  >
                    {{ m.monthName }} '{{ m.year.toString().slice(-2) }} (₹{{ (m.balance || 0).toLocaleString("en-IN") }})
                  </span>
                  <span
                    v-if="s.unpaidMonths.length > 4"
                    class="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-[#111C30] text-slate-500"
                  >
                    +{{ s.unpaidMonths.length - 4 }} more
                  </span>
                </div>
              </div>

              <!-- Last Payment Info -->
              <div class="flex items-center justify-between text-[11px] text-slate-500 dark:text-[#A6B4C8] pt-1 border-t border-slate-100 dark:border-[#263449]">
                <span>Last Payment:</span>
                <span class="font-semibold text-slate-700 dark:text-slate-300">
                  {{ s.lastPayment ? `${formatDate(s.lastPayment.paymentDate)} (₹${s.lastPayment.amount})` : "Never Paid" }}
                </span>
              </div>

              <!-- Mobile Actions Bar (3 Buttons) -->
              <div class="grid grid-cols-3 gap-2 pt-1">
                <button
                  @click="$emit('view-ledger', s._id)"
                  class="px-2 py-1.5 text-xs font-bold rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-[#111C30] dark:hover:bg-[#1E293B] text-slate-700 dark:text-[#F1F5F9] border border-slate-200 dark:border-[#263449] transition text-center cursor-pointer"
                >
                  Ledger
                </button>
                <button
                  @click="$emit('record-payment', s)"
                  class="px-2 py-1.5 text-xs font-bold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white transition text-center cursor-pointer shadow-xs"
                >
                  Collect Fee
                </button>
                <button
                  @click="openReminderModal(s)"
                  class="px-2 py-1.5 text-xs font-bold rounded-xl bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-300 border border-amber-200/80 dark:border-amber-800/60 transition text-center cursor-pointer flex items-center justify-center gap-1"
                >
                  <MessageSquare class="w-3 h-3" />
                  <span>Remind</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Empty State (Mobile) -->
          <div
            v-if="!filteredIrregularStudents.length"
            class="py-12 text-center text-slate-400 bg-slate-50/50 dark:bg-[#0B1120]/40 rounded-2xl border border-slate-200/80 dark:border-[#263449] p-6"
          >
            <CheckCircle class="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <h5 class="text-sm font-bold text-slate-800 dark:text-[#F1F5F9]">All Students Consistent!</h5>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] mt-1">
              No students match the current irregular payment filter criteria.
            </p>
          </div>
        </div>

        <!-- Desktop Table View (hidden md:block) -->
        <div class="hidden md:block overflow-x-auto rounded-xl border border-slate-200/80 dark:border-[#263449]">
          <table class="w-full text-left text-xs min-w-[950px]">
            <thead class="bg-slate-50 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] uppercase font-bold text-[11px] border-b border-slate-200 dark:border-[#263449]">
              <tr>
                <th class="py-3 px-4">Student</th>
                <th class="py-3 px-4">Class</th>
                <th class="py-3 px-4">Consistency Status</th>
                <th class="py-3 px-4">Unpaid Periods</th>
                <th class="py-3 px-4 text-right">Expected</th>
                <th class="py-3 px-4 text-right">Got This</th>
                <th class="py-3 px-4 text-right">Balance Due</th>
                <th class="py-3 px-4">Last Payment</th>
                <th class="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-[#1E293B] font-medium bg-white dark:bg-[#111C30]">
              <tr
                v-for="s in filteredIrregularStudents"
                :key="s._id"
                class="hover:bg-slate-50/70 dark:hover:bg-[#18243A]/50 transition-colors"
              >
                <!-- 1. Student Info -->
                <td class="py-3 px-4">
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-9 h-9 rounded-xl bg-slate-100 dark:bg-[#18243A] flex items-center justify-center text-slate-500 dark:text-[#7E8DA5] overflow-hidden shrink-0 border border-slate-200/80 dark:border-[#263449]"
                    >
                      <img v-if="s.photo" :src="s.photo" alt="Photo" class="w-full h-full object-cover" />
                      <User v-else class="w-4 h-4 text-slate-400" />
                    </div>
                    <div class="min-w-0">
                      <div class="font-black text-slate-900 dark:text-[#F1F5F9] truncate hover:text-indigo-600 transition cursor-pointer" @click="$emit('view-ledger', s._id)">
                        {{ s.name }}
                      </div>
                      <div class="text-[10px] text-slate-400 dark:text-[#7E8DA5] font-mono">
                        Roll: {{ s.rollNumber || "N/A" }} <span v-if="s.phone">• 📞 {{ s.phone }}</span>
                      </div>
                    </div>
                  </div>
                </td>

                <!-- 2. Class -->
                <td class="py-3 px-4 font-semibold text-slate-700 dark:text-slate-300">
                  {{ s.class?.name || "N/A" }}{{ s.class?.section ? ` (${s.class.section})` : "" }}
                </td>

                <!-- 3. Consistency Badge -->
                <td class="py-3 px-4">
                  <span
                    class="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase inline-flex items-center gap-1 shadow-2xs"
                    :class="
                      s.riskLevel === 'high'
                        ? 'bg-red-100 text-red-800 dark:bg-rose-950/60 dark:text-rose-300 border border-red-200 dark:border-rose-900/50'
                        : s.riskLevel === 'medium'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-900/50'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-900/50'
                    "
                  >
                    <AlertCircle v-if="s.riskLevel === 'high'" class="w-3 h-3 text-red-600 dark:text-rose-400" />
                    <Clock v-else class="w-3 h-3 text-amber-600 dark:text-amber-400" />
                    <span>{{ s.consistencyLabel }}</span>
                  </span>
                </td>

                <!-- 4. Unpaid Periods -->
                <td class="py-3 px-4">
                  <div class="flex flex-wrap gap-1 max-w-[200px]">
                    <span
                      v-for="m in (s.unpaidMonths || []).slice(0, 3)"
                      :key="`${m.month}-${m.year}`"
                      class="text-[10px] font-bold font-mono px-2 py-0.5 rounded-md"
                      :class="
                        m.isOverdue
                          ? 'bg-red-50 text-red-700 dark:bg-rose-950/40 dark:text-rose-300 border border-red-200/60 dark:border-rose-900/40'
                          : 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200/60 dark:border-amber-900/40'
                      "
                      :title="`${m.monthName} ${m.year}: Balance ₹${m.balance}`"
                    >
                      {{ m.monthName.slice(0, 3) }} '{{ m.year.toString().slice(-2) }}
                    </span>
                    <span
                      v-if="s.unpaidMonths && s.unpaidMonths.length > 3"
                      class="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-[#18243A] text-slate-500"
                    >
                      +{{ s.unpaidMonths.length - 3 }} more
                    </span>
                    <span v-if="!s.unpaidMonths || !s.unpaidMonths.length" class="text-slate-400 text-[11px]">
                      {{ s.admissionFee?.balance > 0 ? "Admission Fee Due" : "None" }}
                    </span>
                  </div>
                </td>

                <!-- 5. Expected -->
                <td class="py-3 px-4 text-right font-mono font-bold text-slate-700 dark:text-slate-300">
                  ₹{{ (s.totalExpected || 0).toLocaleString("en-IN") }}
                </td>

                <!-- 6. Got This (Collected) -->
                <td class="py-3 px-4 text-right font-mono font-black text-emerald-600 dark:text-emerald-400">
                  ₹{{ (s.totalPaid || 0).toLocaleString("en-IN") }}
                </td>

                <!-- 7. Balance Due -->
                <td class="py-3 px-4 text-right font-mono font-black text-rose-600 dark:text-rose-400">
                  ₹{{ (s.totalOutstanding || 0).toLocaleString("en-IN") }}
                </td>

                <!-- 8. Last Payment -->
                <td class="py-3 px-4 text-slate-600 dark:text-[#A6B4C8] text-[11px]">
                  <div v-if="s.lastPayment">
                    <span class="font-bold text-slate-800 dark:text-slate-200 block">
                      {{ formatDate(s.lastPayment.paymentDate) }}
                    </span>
                    <span class="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono">
                      ₹{{ (s.lastPayment.amount || 0).toLocaleString("en-IN") }} ({{ s.lastPayment.paymentMethod }})
                    </span>
                  </div>
                  <span v-else class="text-slate-400 italic">Never Paid</span>
                </td>

                <!-- 9. Quick Actions -->
                <td class="py-3 px-4 text-right">
                  <div class="inline-flex items-center gap-1.5 justify-end">
                    <button
                      @click="$emit('view-ledger', s._id)"
                      class="px-2 py-1 text-xs font-bold text-slate-700 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#18243A] rounded-lg transition cursor-pointer"
                      title="View Student Fee Ledger"
                    >
                      Ledger
                    </button>
                    <button
                      @click="$emit('record-payment', s)"
                      class="px-2.5 py-1 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-2xs transition cursor-pointer"
                      title="Record Payment"
                    >
                      Collect
                    </button>
                    <button
                      @click="openReminderModal(s)"
                      class="p-1 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40 rounded-lg transition cursor-pointer"
                      title="Send / Copy Reminder Notice"
                    >
                      <MessageSquare class="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="!filteredIrregularStudents.length">
                <td colspan="9" class="py-12 text-center text-slate-400">
                  <CheckCircle class="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                  <p class="font-bold text-slate-700 dark:text-slate-300 text-sm">
                    All students are paying consistently!
                  </p>
                  <p class="text-xs text-slate-400 mt-1">
                    No students match the current irregular payment filter criteria.
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- REMINDER NOTICE DIALOG / MODAL -->
    <div
      v-if="showReminderModal"
      class="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4"
    >
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200 dark:border-[#263449] max-w-lg w-full p-5 sm:p-6 space-y-4 shadow-xl relative animate-in fade-in zoom-in-95 duration-150"
      >
        <div class="flex items-start justify-between">
          <div class="flex items-center space-x-3">
            <div
              class="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 border border-amber-200/80 dark:border-amber-800/40"
            >
              <MessageSquare class="w-5 h-5" />
            </div>
            <div>
              <h4 class="text-base font-black text-slate-900 dark:text-[#F1F5F9]">
                Fee Payment Reminder
              </h4>
              <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
                For {{ reminderTargetStudent?.name }} (Roll: {{ reminderTargetStudent?.rollNumber }})
              </p>
            </div>
          </div>
          <button
            @click="showReminderModal = false"
            class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
          >
            ✕
          </button>
        </div>

        <!-- Student Summary Pill -->
        <div class="bg-slate-50 dark:bg-[#18243A] p-3 rounded-xl border border-slate-200/80 dark:border-[#263449] space-y-1.5 text-xs">
          <div class="flex items-center justify-between">
            <span class="text-slate-500 dark:text-[#A6B4C8]">Total Outstanding:</span>
            <span class="font-mono font-black text-rose-600 dark:text-rose-400 text-sm">
              ₹{{ (reminderTargetStudent?.totalOutstanding || 0).toLocaleString("en-IN") }}
            </span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-slate-500 dark:text-[#A6B4C8]">Overdue Months:</span>
            <span class="font-semibold text-slate-800 dark:text-[#F1F5F9]">
              {{ reminderTargetStudent?.unpaidMonths?.length || 0 }} Month(s)
            </span>
          </div>
          <div v-if="reminderTargetStudent?.phone" class="flex items-center justify-between">
            <span class="text-slate-500 dark:text-[#A6B4C8]">Registered Phone:</span>
            <span class="font-mono font-bold text-slate-800 dark:text-[#F1F5F9]">
              {{ reminderTargetStudent.phone }}
            </span>
          </div>
        </div>

        <!-- Message Box -->
        <div>
          <label class="text-[11px] font-bold text-slate-500 dark:text-[#A6B4C8] uppercase tracking-wider block mb-1">
            Notice Template Message
          </label>
          <textarea
            v-model="reminderMessageText"
            rows="5"
            class="w-full p-3 text-xs font-mono rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] outline-none resize-none focus:ring-2 focus:ring-indigo-500/20"
          ></textarea>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col sm:flex-row items-center justify-end gap-2 pt-2">
          <button
            @click="copyReminderNotice"
            class="w-full sm:w-auto px-4 py-2 text-xs font-bold rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#172235] text-slate-700 dark:text-[#F1F5F9] border border-slate-200 dark:border-[#263449] transition flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <Check v-if="hasCopiedNotice" class="w-4 h-4 text-emerald-500" />
            <span v-else>📋</span>
            <span>{{ hasCopiedNotice ? "Copied to Clipboard!" : "Copy Notice Text" }}</span>
          </button>

          <a
            v-if="reminderWhatsAppUrl"
            :href="reminderWhatsAppUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="w-full sm:w-auto px-4 py-2 text-xs font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition flex items-center justify-center gap-1.5 cursor-pointer shadow-md shadow-emerald-600/20"
          >
            <span>💬</span>
            <span>Open in WhatsApp</span>
          </a>

          <button
            @click="showReminderModal = false"
            class="w-full sm:w-auto px-4 py-2 text-xs font-bold rounded-xl bg-slate-200 hover:bg-slate-300 dark:bg-[#263449] dark:hover:bg-[#324560] text-slate-700 dark:text-slate-200 transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
  import {
    BarChart3,
    TrendingUp,
    CheckCircle,
    CheckCircle2,
    AlertCircle,
    AlertTriangle,
    RefreshCw,
    Search,
    Users,
    PieChart,
    Award,
    ArrowUpDown,
    ChevronRight,
    MessageSquare,
    User,
    Clock,
    Check,
    Building2,
    CalendarDays,
    CalendarRange,
  } from "lucide-vue-next";
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    BarController,
  } from "chart.js";
  import { getFeeAnalytics } from "@/services/feeService";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  // Register Chart.js modules
  ChartJS.register(
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    BarController
  );

  const props = defineProps({
    classesList: {
      type: Array,
      default: () => [],
    },
    availableSessions: {
      type: Array,
      default: () => [],
    },
    currentSession: {
      type: String,
      default: "",
    },
  });

  defineEmits(["view-ledger", "record-payment", "open-class-fee"]);

  const { showToast } = useGlobalToast();

  // State
  const isLoading = ref(false);
  const activeSession = ref(props.currentSession || "");
  const chartMode = ref("class"); // "class" | "monthly" | "yearly"
  const feeScope = ref("total"); // "total" | "monthly" | "admission"
  const chartOrientation = ref("vertical"); // "vertical" | "horizontal"
  const chartSort = ref("order"); // "order" | "expected_desc" | "collected_desc" | "deficit_desc" | "rate_desc" | "rate_asc"
  const selectedClassMonth = ref(0); // 0 = all/annual, 1..12 = specific month
  const selectedMonthlyClass = ref(""); // "" = all classes, or classId

  const classesData = ref([]);
  const monthlyData = ref([]);
  const yearlyData = ref([]);
  const irregularStudents = ref([]);
  const summary = ref({
    totalExpected: 0,
    totalCollected: 0,
    totalOutstanding: 0,
    overallCollectionRate: 0,
    totalAdmissionExpected: 0,
    totalAdmissionCollected: 0,
    totalMonthlyExpected: 0,
    totalMonthlyCollected: 0,
    monthlyAverageExpected: 0,
    monthlyAverageCollected: 0,
    yearlyExpected: 0,
    yearlyCollected: 0,
    totalStudentsCount: 0,
    consistentStudentsCount: 0,
    totalIrregularStudentsCount: 0,
    chronicDefaultersCount: 0,
    irregularPayersCount: 0,
    delayedPayersCount: 0,
    bestPerformingClass: null,
    lowestPerformingClass: null,
    bestMonth: null,
    lowestMonth: null,
  });

  // Chart Canvas & Instance
  const chartCanvas = ref(null);
  let chartInstance = null;

  // Defaulters Section Filters
  const defaultersSectionRef = ref(null);
  const defaulterRiskFilter = ref("all"); // "all" | "high" | "medium" | "low"
  const defaulterClassFilter = ref("");
  const defaulterSearchQuery = ref("");
  const defaulterSort = ref("balance_desc");

  // Reminder Notice Modal
  const showReminderModal = ref(false);
  const reminderTargetStudent = ref(null);
  const reminderMessageText = ref("");
  const hasCopiedNotice = ref(false);

  // Helper getters for fee scope on classes
  const getScopeExpected = (cls) => {
    if (feeScope.value === "monthly") return cls.monthlyExpected || 0;
    if (feeScope.value === "admission") return cls.admissionExpected || 0;
    return cls.totalExpected || 0;
  };

  const getScopeCollected = (cls) => {
    if (feeScope.value === "monthly") return cls.monthlyCollected || 0;
    if (feeScope.value === "admission") return cls.admissionCollected || 0;
    return cls.totalCollected || 0;
  };

  const getScopeOutstanding = (cls) => {
    if (feeScope.value === "monthly") return cls.monthlyBalance || 0;
    if (feeScope.value === "admission") return cls.admissionBalance || 0;
    return cls.totalOutstanding || 0;
  };

  // Sorted Classes based on user sort selection
  const sortedClasses = computed(() => {
    const list = [...classesData.value];
    switch (chartSort.value) {
      case "expected_desc":
        return list.sort((a, b) => getScopeExpected(b) - getScopeExpected(a));
      case "collected_desc":
        return list.sort((a, b) => getScopeCollected(b) - getScopeCollected(a));
      case "deficit_desc":
        return list.sort((a, b) => getScopeOutstanding(b) - getScopeOutstanding(a));
      case "rate_desc":
        return list.sort((a, b) => b.collectionRate - a.collectionRate);
      case "rate_asc":
        return list.sort((a, b) => a.collectionRate - b.collectionRate);
      case "order":
      default:
        return list.sort((a, b) => {
          if (a.order !== b.order) return (a.order || 999) - (b.order || 999);
          return a.displayName.localeCompare(b.displayName);
        });
    }
  });

  // Dynamic Chart Height based on orientation, count, and mode
  const chartContainerHeight = computed(() => {
    if (chartOrientation.value === "horizontal") {
      let rowCount = 6;
      if (chartMode.value === "class") rowCount = sortedClasses.value.length || 6;
      else if (chartMode.value === "monthly") rowCount = 12;
      else if (chartMode.value === "yearly") rowCount = yearlyData.value.length || 4;
      return `${Math.max(340, rowCount * 36)}px`;
    }
    return "380px";
  });

  // Filtered Irregular Students
  const filteredIrregularStudents = computed(() => {
    let list = [...irregularStudents.value];

    // Filter by risk
    if (defaulterRiskFilter.value !== "all") {
      list = list.filter((s) => s.riskLevel === defaulterRiskFilter.value);
    }

    // Filter by class
    if (defaulterClassFilter.value) {
      list = list.filter((s) => {
        const sClassId = s.class?._id || s.class;
        return sClassId && sClassId.toString() === defaulterClassFilter.value.toString();
      });
    }

    // Filter by search query
    if (defaulterSearchQuery.value && defaulterSearchQuery.value.trim()) {
      const q = defaulterSearchQuery.value.toLowerCase().trim();
      list = list.filter(
        (s) =>
          (s.name && s.name.toLowerCase().includes(q)) ||
          (s.rollNumber && s.rollNumber.toString().toLowerCase().includes(q)) ||
          (s.phone && s.phone.includes(q))
      );
    }

    // Sort
    switch (defaulterSort.value) {
      case "balance_desc":
        return list.sort((a, b) => b.totalOutstanding - a.totalOutstanding);
      case "overdue_desc":
        return list.sort(
          (a, b) => (b.unpaidMonths?.length || 0) - (a.unpaidMonths?.length || 0)
        );
      case "score_asc":
        return list.sort((a, b) => a.regularityScore - b.regularityScore);
      case "name_asc":
        return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      case "roll_asc":
        return list.sort((a, b) => (a.rollNumber || "").localeCompare(b.rollNumber || "", undefined, { numeric: true }));
      default:
        return list;
    }
  });

  // Fetch Analytics API
  const fetchAnalytics = async () => {
    try {
      isLoading.value = true;
      const res = await getFeeAnalytics({
        session: activeSession.value || undefined,
      });

      if (res.data?.success) {
        classesData.value = res.data.data.classes || [];
        monthlyData.value = res.data.data.monthlyBreakdown || [];
        yearlyData.value = res.data.data.yearlyBreakdown || [];
        irregularStudents.value = res.data.data.irregularStudents || [];
        summary.value = res.data.data.summary || {
          totalExpected: 0,
          totalCollected: 0,
          totalOutstanding: 0,
          overallCollectionRate: 0,
          totalAdmissionExpected: 0,
          totalAdmissionCollected: 0,
          totalMonthlyExpected: 0,
          totalMonthlyCollected: 0,
          monthlyAverageExpected: 0,
          monthlyAverageCollected: 0,
          yearlyExpected: 0,
          yearlyCollected: 0,
          totalStudentsCount: 0,
          consistentStudentsCount: 0,
          totalIrregularStudentsCount: 0,
          chronicDefaultersCount: 0,
          irregularPayersCount: 0,
          delayedPayersCount: 0,
          bestPerformingClass: null,
          lowestPerformingClass: null,
          bestMonth: null,
          lowestMonth: null,
        };

        await nextTick();
        renderChart();
      }
    } catch (err) {
      console.error("fetchAnalytics error:", err);
      showToast("error", "Failed to load fee analytics");
    } finally {
      isLoading.value = false;
    }
  };

  // Toggle Chart Orientation
  const toggleOrientation = () => {
    chartOrientation.value =
      chartOrientation.value === "vertical" ? "horizontal" : "vertical";
    nextTick(() => {
      renderChart();
    });
  };

  // Render Modern Chart.js Graph (Multi-Mode: Class | Monthly | Yearly)
  const renderChart = () => {
    if (!chartCanvas.value) return;

    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }

    const isDark = document.documentElement.classList.contains("dark");
    const textColor = isDark ? "#A6B4C8" : "#475569";
    const gridColor = isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)";

    let labels = [];
    let expectedData = [];
    let collectedData = [];

    if (chartMode.value === "class") {
      if (selectedClassMonth.value > 0) {
        const mSlot = monthlyData.value.find((m) => m.month === selectedClassMonth.value);
        labels = sortedClasses.value.map((c) => c.displayName);
        expectedData = sortedClasses.value.map((c) => {
          const stats = mSlot?.byClass?.[c.classId];
          return stats ? stats.expected : 0;
        });
        collectedData = sortedClasses.value.map((c) => {
          const stats = mSlot?.byClass?.[c.classId];
          return stats ? stats.collected : 0;
        });
      } else {
        labels = sortedClasses.value.map((c) => c.displayName);
        expectedData = sortedClasses.value.map((c) => getScopeExpected(c));
        collectedData = sortedClasses.value.map((c) => getScopeCollected(c));
      }
    } else if (chartMode.value === "monthly") {
      labels = monthlyData.value.map((m) => m.shortMonth);
      if (selectedMonthlyClass.value) {
        const targetClassId = selectedMonthlyClass.value;
        expectedData = monthlyData.value.map((m) => m.byClass?.[targetClassId]?.expected || 0);
        collectedData = monthlyData.value.map((m) => m.byClass?.[targetClassId]?.collected || 0);
      } else {
        expectedData = monthlyData.value.map((m) => m.expected || 0);
        collectedData = monthlyData.value.map((m) => m.collected || 0);
      }
    } else if (chartMode.value === "yearly") {
      labels = yearlyData.value.map((y) => y.session + (y.isCurrent ? " (Active)" : ""));
      if (feeScope.value === "monthly") {
        expectedData = yearlyData.value.map((y) => y.monthlyExpected || 0);
        collectedData = yearlyData.value.map((y) => y.monthlyCollected || 0);
      } else if (feeScope.value === "admission") {
        expectedData = yearlyData.value.map((y) => y.admissionExpected || 0);
        collectedData = yearlyData.value.map((y) => y.admissionCollected || 0);
      } else {
        expectedData = yearlyData.value.map((y) => y.totalExpected || 0);
        collectedData = yearlyData.value.map((y) => y.totalCollected || 0);
      }
    }

    const ctx = chartCanvas.value.getContext("2d");

    // Create subtle modern gradients
    let expectedGradient = "rgba(99, 102, 241, 0.85)";
    let collectedGradient = "rgba(16, 185, 129, 0.85)";

    if (ctx) {
      if (chartOrientation.value === "vertical") {
        const g1 = ctx.createLinearGradient(0, 0, 0, 360);
        g1.addColorStop(0, "rgba(99, 102, 241, 0.95)");
        g1.addColorStop(1, "rgba(129, 140, 248, 0.75)");
        expectedGradient = g1;

        const g2 = ctx.createLinearGradient(0, 0, 0, 360);
        g2.addColorStop(0, "rgba(16, 185, 129, 0.95)");
        g2.addColorStop(1, "rgba(52, 211, 153, 0.75)");
        collectedGradient = g2;
      } else {
        const g1 = ctx.createLinearGradient(0, 0, 500, 0);
        g1.addColorStop(0, "rgba(99, 102, 241, 0.75)");
        g1.addColorStop(1, "rgba(99, 102, 241, 0.95)");
        expectedGradient = g1;

        const g2 = ctx.createLinearGradient(0, 0, 500, 0);
        g2.addColorStop(0, "rgba(16, 185, 129, 0.75)");
        g2.addColorStop(1, "rgba(16, 185, 129, 0.95)");
        collectedGradient = g2;
      }
    }

    const isHorizontal = chartOrientation.value === "horizontal";

    chartInstance = new ChartJS(chartCanvas.value, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Expected Fee",
            data: expectedData,
            backgroundColor: expectedGradient,
            borderColor: "rgb(99, 102, 241)",
            borderWidth: 1.5,
            borderRadius: 6,
            maxBarThickness: 32,
          },
          {
            label: "Got / Received Fee",
            data: collectedData,
            backgroundColor: collectedGradient,
            borderColor: "rgb(16, 185, 129)",
            borderWidth: 1.5,
            borderRadius: 6,
            maxBarThickness: 32,
          },
        ],
      },
      options: {
        indexAxis: isHorizontal ? "y" : "x",
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 600,
          easing: "easeOutQuart",
        },
        plugins: {
          legend: {
            display: false, // Custom modern legend above
          },
          tooltip: {
            backgroundColor: isDark ? "#172235" : "#0F172A",
            titleColor: "#FFFFFF",
            bodyColor: "#E2E8F0",
            padding: 12,
            cornerRadius: 12,
            boxPadding: 4,
            usePointStyle: true,
            callbacks: {
              title: (items) => {
                const idx = items[0]?.dataIndex;
                if (chartMode.value === "class") {
                  const cls = sortedClasses.value[idx];
                  const monthPrefix =
                    selectedClassMonth.value > 0
                      ? `${monthlyData.value.find((m) => m.month === selectedClassMonth.value)?.monthName || ""} • `
                      : "";
                  return cls ? `${monthPrefix}${cls.displayName} (Enrolled: ${cls.totalStudents})` : "";
                } else if (chartMode.value === "monthly") {
                  const m = monthlyData.value[idx];
                  const classSuffix = selectedMonthlyClass.value
                    ? ` (${classesData.value.find((c) => c.classId === selectedMonthlyClass.value)?.displayName || "Class"})`
                    : "";
                  return m ? `${m.monthName}${classSuffix}` : "";
                } else if (chartMode.value === "yearly") {
                  const y = yearlyData.value[idx];
                  return y ? `Session: ${y.session}${y.isCurrent ? " (Active Session)" : ""}` : "";
                }
                return "";
              },
              label: (context) => {
                const label = context.dataset.label || "";
                const val = Number(context.parsed[isHorizontal ? "x" : "y"]) || 0;
                return `${label}: ₹${val.toLocaleString("en-IN")}`;
              },
              afterBody: (items) => {
                const idx = items[0]?.dataIndex;
                if (idx === undefined || idx === null) return [];

                const exp = expectedData[idx] || 0;
                const rec = collectedData[idx] || 0;
                const gap = Math.max(0, exp - rec);
                const pct = exp > 0 ? ((rec / exp) * 100).toFixed(1) : "0.0";

                const lines = [
                  `Shortfall / Gap: ₹${gap.toLocaleString("en-IN")}`,
                  `Realization Rate: ${pct}%`,
                ];

                if (chartMode.value === "monthly") {
                  const m = monthlyData.value[idx];
                  if (m && !selectedMonthlyClass.value) {
                    lines.push(`Students Due: ${m.studentCount || 0}`);
                  }
                } else if (chartMode.value === "yearly") {
                  const y = yearlyData.value[idx];
                  if (y) {
                    lines.push(`Students: ${y.studentsCount || 0} • Transactions: ${y.transactionCount || 0}`);
                  }
                }
                return lines;
              },
            },
          },
        },
        scales: {
          x: {
            grid: {
              color: isHorizontal ? gridColor : "transparent",
              drawBorder: false,
            },
            ticks: {
              color: textColor,
              font: {
                size: 11,
                weight: "bold",
              },
              callback: (val) => {
                if (isHorizontal) {
                  return Number(val) >= 1000 ? `₹${(Number(val) / 1000).toFixed(0)}k` : `₹${val}`;
                }
                return val;
              },
            },
          },
          y: {
            grid: {
              color: !isHorizontal ? gridColor : "transparent",
              drawBorder: false,
            },
            ticks: {
              color: textColor,
              font: {
                size: 11,
                weight: "bold",
              },
              callback: (val) => {
                if (!isHorizontal) {
                  return Number(val) >= 1000 ? `₹${(Number(val) / 1000).toFixed(0)}k` : `₹${val}`;
                }
                return val;
              },
            },
          },
        },
      },
    });
  };

  // Filter Defaulters to a specific class and scroll smoothly
  const filterDefaultersByClass = (classId) => {
    defaulterClassFilter.value = classId ? classId.toString() : "";
    if (defaultersSectionRef.value) {
      defaultersSectionRef.value.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const resetDefaulterFilters = () => {
    defaulterRiskFilter.value = "all";
    defaulterClassFilter.value = "";
    defaulterSearchQuery.value = "";
    defaulterSort.value = "balance_desc";
  };

  // Open Payment Reminder Modal
  const openReminderModal = (student) => {
    reminderTargetStudent.value = student;
    hasCopiedNotice.value = false;

    const unpaidMonthsList = (student.unpaidMonths || [])
      .map((m) => `${m.monthName} ${m.year}`)
      .join(", ");

    reminderMessageText.value =
      `Dear Parent / Guardian,\n\n` +
      `This is a gentle fee reminder regarding ${student.name} (Class: ${student.class?.name || "N/A"}` +
      (student.class?.section ? ` - ${student.class.section}` : "") +
      `, Roll No: ${student.rollNumber || "N/A"}).\n\n` +
      `Total Pending Balance: ₹${(student.totalOutstanding || 0).toLocaleString("en-IN")}\n` +
      (unpaidMonthsList ? `Pending Period(s): ${unpaidMonthsList}\n\n` : `\n`) +
      `Kindly clear the outstanding dues at your earliest convenience to maintain an up-to-date ledger.\n\n` +
      `Thank you.\nSchool Administration`;

    showReminderModal.value = true;
  };

  const copyReminderNotice = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(reminderMessageText.value);
        hasCopiedNotice.value = true;
        showToast("success", "Notice copied to clipboard!");
        setTimeout(() => {
          hasCopiedNotice.value = false;
        }, 3000);
      }
    } catch (err) {
      console.error("copyReminderNotice error:", err);
    }
  };

  const reminderWhatsAppUrl = computed(() => {
    if (!reminderTargetStudent.value?.phone) return null;
    const cleanPhone = reminderTargetStudent.value.phone.replace(/\D/g, "");
    const phoneWithCountry = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;
    const text = encodeURIComponent(reminderMessageText.value);
    return `https://wa.me/${phoneWithCountry}?text=${text}`;
  });

  const formatDate = (d) => {
    if (!d) return "N/A";
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Watchers to re-render chart on scope/sort/mode/filter changes
  watch(
    [feeScope, chartSort, chartMode, selectedClassMonth, selectedMonthlyClass],
    () => {
      nextTick(() => {
        renderChart();
      });
    }
  );

  watch(
    () => props.currentSession,
    (newVal) => {
      if (newVal !== activeSession.value) {
        activeSession.value = newVal || "";
        fetchAnalytics();
      }
    }
  );

  // ResizeObserver to handle responsiveness
  let resizeObserver = null;

  onMounted(() => {
    fetchAnalytics();

    if (window.ResizeObserver && chartCanvas.value) {
      resizeObserver = new ResizeObserver(() => {
        if (chartInstance) {
          chartInstance.resize();
        }
      });
      if (chartCanvas.value.parentElement) {
        resizeObserver.observe(chartCanvas.value.parentElement);
      }
    }
  });

  onBeforeUnmount(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }
  });
</script>
